import React, { useContext, useState, useEffect } from 'react';
import { AuthContext } from '../App';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { toast } from 'sonner';
import { BookOpen, Search, Calendar, User, Loader2, Lock, Plus, Trash2, ChevronDown, ChevronUp, Video, ExternalLink, Radio, Play, X, ListVideo, Edit, Save } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';

// Función para extraer ID de YouTube de una URL
const getYouTubeId = (url) => {
  if (!url) return null;
  const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
  const match = url.match(regExp);
  return (match && match[2].length === 11) ? match[2] : null;
};

// Función para verificar si es una playlist de YouTube
const getYouTubePlaylistId = (url) => {
  if (!url) return null;
  const regExp = /[?&]list=([^#&?]+)/;
  const match = url.match(regExp);
  return match ? match[1] : null;
};

const GraduadosDevocionales = () => {
  const { user, API } = useContext(AuthContext);
  const [devocionales, setDevocionales] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedId, setExpandedId] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showVideoModal, setShowVideoModal] = useState(false);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [editingDevocional, setEditingDevocional] = useState(null);
  const [formData, setFormData] = useState({
    titulo: '',
    contenido: '',
    versiculo: '',
    cita_biblica: '',
    autor: '',
    video_url: '',
    playlist_url: '',
    zoom_url: ''
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadDevocionales();
  }, []);

  const loadDevocionales = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/graduados/devocionales`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setDevocionales(data.devocionales || []);
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error al cargar devocionales');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      titulo: '',
      contenido: '',
      versiculo: '',
      cita_biblica: '',
      autor: '',
      video_url: '',
      playlist_url: '',
      zoom_url: ''
    });
  };

  const openAddModal = () => {
    resetForm();
    setShowAddModal(true);
  };

  const openEditModal = (devocional) => {
    setEditingDevocional(devocional);
    setFormData({
      titulo: devocional.titulo || '',
      contenido: devocional.contenido || '',
      versiculo: devocional.versiculo || '',
      cita_biblica: devocional.cita_biblica || '',
      autor: devocional.autor || '',
      video_url: devocional.video_url || '',
      playlist_url: devocional.playlist_url || '',
      zoom_url: devocional.zoom_url || ''
    });
    setShowEditModal(true);
  };

  const handleSaveDevocional = async () => {
    if (!formData.titulo || !formData.contenido) {
      toast.error('Título y contenido son requeridos');
      return;
    }
    
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/graduados/devocionales`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Error al guardar');
      }
      
      toast.success('Devocional añadido exitosamente');
      setShowAddModal(false);
      resetForm();
      await loadDevocionales(); // Recargar lista
    } catch (error) {
      console.error('Error:', error);
      toast.error(error.message || 'Error al guardar el devocional');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateDevocional = async () => {
    if (!formData.titulo || !formData.contenido) {
      toast.error('Título y contenido son requeridos');
      return;
    }
    
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/graduados/devocionales/${editingDevocional.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Error al actualizar');
      }
      
      toast.success('Devocional actualizado exitosamente');
      setShowEditModal(false);
      setEditingDevocional(null);
      resetForm();
      await loadDevocionales(); // Recargar lista
    } catch (error) {
      console.error('Error:', error);
      toast.error(error.message || 'Error al actualizar el devocional');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteDevocional = async (id) => {
    if (!window.confirm('¿Eliminar este devocional?')) return;
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/graduados/devocionales/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!response.ok) throw new Error('Error al eliminar');
      
      toast.success('Devocional eliminado');
      await loadDevocionales();
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  const openVideoPlayer = (devocional, isPlaylist = false) => {
    setSelectedVideo({ ...devocional, showPlaylist: isPlaylist });
    setShowVideoModal(true);
  };

  const filteredDevocionales = devocionales.filter(d =>
    d.titulo?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    d.contenido?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    d.autor?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Renderizar el reproductor embebido
  const renderEmbeddedPlayer = (url, isPlaylist = false) => {
    if (!url) return null;
    
    const playlistId = getYouTubePlaylistId(url);
    const youtubeId = getYouTubeId(url);
    
    if (isPlaylist && playlistId) {
      return (
        <iframe
          className="w-full aspect-video rounded-lg"
          src={`https://www.youtube.com/embed/videoseries?list=${playlistId}`}
          title="YouTube Playlist"
          frameBorder="0"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
        />
      );
    } else if (playlistId) {
      // URL tiene playlist, mostrar como playlist
      return (
        <iframe
          className="w-full aspect-video rounded-lg"
          src={`https://www.youtube.com/embed/videoseries?list=${playlistId}`}
          title="YouTube Playlist"
          frameBorder="0"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
        />
      );
    } else if (youtubeId) {
      return (
        <iframe
          className="w-full aspect-video rounded-lg"
          src={`https://www.youtube.com/embed/${youtubeId}`}
          title="YouTube Video"
          frameBorder="0"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
        />
      );
    } else {
      return (
        <div className="w-full aspect-video bg-gray-100 rounded-lg flex flex-col items-center justify-center">
          <Video className="w-16 h-16 text-gray-400 mb-4" />
          <a
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700"
          >
            <ExternalLink className="w-4 h-4" />
            Abrir video
          </a>
        </div>
      );
    }
  };

  // Formulario compartido para crear/editar
  const renderForm = () => (
    <div className="space-y-4 py-4">
      <div>
        <label className="text-sm font-medium text-gray-700">Título *</label>
        <Input
          value={formData.titulo}
          onChange={(e) => setFormData({ ...formData, titulo: e.target.value })}
          placeholder="Título del devocional"
        />
      </div>
      
      <div>
        <label className="text-sm font-medium text-gray-700">Cita Bíblica</label>
        <Input
          value={formData.cita_biblica}
          onChange={(e) => setFormData({ ...formData, cita_biblica: e.target.value })}
          placeholder="ej: Juan 3:16"
        />
      </div>
      
      <div>
        <label className="text-sm font-medium text-gray-700">Versículo</label>
        <textarea
          value={formData.versiculo}
          onChange={(e) => setFormData({ ...formData, versiculo: e.target.value })}
          placeholder="Texto del versículo..."
          className="w-full p-2 border border-gray-300 rounded-md text-sm"
          rows={2}
        />
      </div>
      
      <div>
        <label className="text-sm font-medium text-gray-700">Contenido *</label>
        <textarea
          value={formData.contenido}
          onChange={(e) => setFormData({ ...formData, contenido: e.target.value })}
          placeholder="Reflexión del devocional..."
          className="w-full p-2 border border-gray-300 rounded-md text-sm"
          rows={5}
        />
      </div>
      
      <div>
        <label className="text-sm font-medium text-gray-700">Autor</label>
        <Input
          value={formData.autor}
          onChange={(e) => setFormData({ ...formData, autor: e.target.value })}
          placeholder="Nombre del autor"
        />
      </div>
      
      <div className="border-t pt-4 mt-4">
        <h4 className="text-sm font-semibold text-gray-800 mb-3 flex items-center gap-2">
          <Video className="w-4 h-4" />
          Contenido Multimedia (Opcional)
        </h4>
        
        <div className="space-y-3">
          <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
            <label className="text-sm font-medium text-amber-800 flex items-center gap-2">
              <ListVideo className="w-4 h-4" />
              Playlist de YouTube (Recomendado)
            </label>
            <Input
              value={formData.playlist_url}
              onChange={(e) => setFormData({ ...formData, playlist_url: e.target.value })}
              placeholder="https://www.youtube.com/playlist?list=PLxxxxxxx"
              className="mt-2"
            />
            <p className="text-xs text-amber-700 mt-1">
              📋 Los videos de la playlist se mostrarán como tarjetas con imagen.
            </p>
          </div>
          
          <div>
            <label className="text-sm font-medium text-gray-700">Video Individual (YouTube)</label>
            <Input
              value={formData.video_url}
              onChange={(e) => setFormData({ ...formData, video_url: e.target.value })}
              placeholder="https://www.youtube.com/watch?v=xxxxx"
            />
          </div>
          
          <div>
            <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
              <Radio className="w-4 h-4 text-red-500" />
              Link de Zoom (EN VIVO)
            </label>
            <Input
              value={formData.zoom_url}
              onChange={(e) => setFormData({ ...formData, zoom_url: e.target.value })}
              placeholder="https://zoom.us/j/..."
            />
          </div>
        </div>
      </div>
    </div>
  );

  if (!user?.es_graduado && user?.rol !== 'admin') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-amber-50 to-orange-100 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-6 text-center">
            <div className="w-20 h-20 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Lock className="w-10 h-10 text-amber-600" />
            </div>
            <h2 className="text-xl font-bold text-gray-800 mb-2">Contenido Exclusivo</h2>
            <p className="text-gray-600">
              Los devocionales son exclusivos para graduados del Instituto Bíblico Apostólico.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Verificar si hay algún devocional con Zoom activo
  const devocionalConZoom = devocionales.find(d => d.zoom_url);

  // Separar devocionales con playlist
  const devocionalesConPlaylist = filteredDevocionales.filter(d => d.playlist_url);
  const devocionalesSinPlaylist = filteredDevocionales.filter(d => !d.playlist_url);

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 to-orange-100 p-4 md:p-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 bg-gradient-to-br from-amber-500 to-orange-600 rounded-xl flex items-center justify-center shadow-lg">
            <BookOpen className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-800">Devocionales</h1>
            <p className="text-gray-600 text-sm">Reflexiones espirituales para graduados</p>
          </div>
        </div>
        
        {user?.rol === 'admin' && (
          <Button
            onClick={openAddModal}
            className="bg-gradient-to-r from-amber-600 to-orange-600 hover:from-amber-700 hover:to-orange-700"
          >
            <Plus className="w-4 h-4 mr-2" />
            Añadir Devocional
          </Button>
        )}
      </div>

      {/* Banner de Zoom en Vivo */}
      {devocionalConZoom && (
        <Card className="mb-6 border-2 border-red-400 bg-gradient-to-r from-red-50 to-orange-50">
          <CardContent className="p-4">
            <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
              <div className="flex items-start gap-3 flex-1">
                <div className="w-10 h-10 bg-red-500 rounded-full flex items-center justify-center animate-pulse flex-shrink-0 mt-1">
                  <Radio className="w-5 h-5 text-white" />
                </div>
                <div className="flex-1">
                  <h3 className="font-bold text-red-700">🔴 Devocional EN VIVO</h3>
                  <p className="text-gray-800 font-medium">{devocionalConZoom.titulo}</p>
                  {devocionalConZoom.contenido && (
                    <p className="text-sm text-gray-600 mt-1 whitespace-pre-line">
                      {devocionalConZoom.contenido}
                    </p>
                  )}
                  {devocionalConZoom.cita_biblica && (
                    <p className="text-sm text-amber-700 mt-1">📖 {devocionalConZoom.cita_biblica}</p>
                  )}
                </div>
              </div>
              <a
                href={devocionalConZoom.zoom_url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-2 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium flex-shrink-0"
              >
                <Video className="w-4 h-4" />
                Unirse a Zoom
              </a>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Search */}
      <Card className="mb-6">
        <CardContent className="p-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <Input
              placeholder="Buscar devocionales..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
        </CardContent>
      </Card>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-amber-600" />
        </div>
      ) : filteredDevocionales.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <BookOpen className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">
              {searchTerm ? 'No se encontraron devocionales' : 'Aún no hay devocionales disponibles'}
            </p>
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Sección de Videos/Playlists */}
          {devocionalesConPlaylist.length > 0 && (
            <div className="mb-8">
              <h2 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
                <ListVideo className="w-5 h-5 text-amber-600" />
                Videos de Devocionales
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {devocionalesConPlaylist.map((devocional) => {
                  const playlistId = getYouTubePlaylistId(devocional.playlist_url);
                  const videoId = getYouTubeId(devocional.playlist_url);
                  
                  return (
                    <Card 
                      key={devocional.id} 
                      className="hover:shadow-lg transition-shadow overflow-hidden group cursor-pointer"
                      onClick={() => openVideoPlayer(devocional, true)}
                    >
                      <CardContent className="p-0">
                        {/* Thumbnail */}
                        <div className="relative bg-gradient-to-br from-amber-800 to-orange-900 h-40 flex items-center justify-center">
                          {videoId ? (
                            <img
                              src={`https://img.youtube.com/vi/${videoId}/mqdefault.jpg`}
                              alt={devocional.titulo}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <div className="flex flex-col items-center text-amber-200">
                              <ListVideo className="w-12 h-12 mb-2" />
                              <span className="text-sm">Playlist de YouTube</span>
                            </div>
                          )}
                          <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                            <div className="w-14 h-14 bg-white/90 rounded-full flex items-center justify-center">
                              <Play className="w-6 h-6 text-amber-600 ml-1" />
                            </div>
                          </div>
                          <span className="absolute top-2 left-2 bg-amber-600 text-white text-xs px-2 py-1 rounded-full flex items-center gap-1">
                            <ListVideo className="w-3 h-3" /> Playlist
                          </span>
                        </div>
                        
                        <div className="p-4">
                          <h3 className="font-bold text-gray-800 mb-1 line-clamp-2">{devocional.titulo}</h3>
                          {devocional.contenido && (
                            <p className="text-sm text-gray-600 mb-2 line-clamp-2">{devocional.contenido}</p>
                          )}
                          {devocional.cita_biblica && (
                            <p className="text-sm text-amber-700 mb-2">📖 {devocional.cita_biblica}</p>
                          )}
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2 text-xs text-gray-500">
                              <Calendar className="w-3 h-3" />
                              <span>{devocional.fecha_creacion ? new Date(devocional.fecha_creacion).toLocaleDateString('es-UY') : 'Sin fecha'}</span>
                            </div>
                            
                            {user?.rol === 'admin' && (
                              <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                                <button
                                  onClick={() => openEditModal(devocional)}
                                  className="text-blue-500 hover:text-blue-700 p-1"
                                  title="Editar"
                                >
                                  <Edit className="w-4 h-4" />
                                </button>
                                <button
                                  onClick={() => handleDeleteDevocional(devocional.id)}
                                  className="text-red-500 hover:text-red-700 p-1"
                                  title="Eliminar"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              </div>
                            )}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </div>
          )}

          {/* Sección de Devocionales Escritos */}
          {devocionalesSinPlaylist.length > 0 && (
            <div>
              {devocionalesConPlaylist.length > 0 && (
                <h2 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
                  <BookOpen className="w-5 h-5 text-amber-600" />
                  Reflexiones Escritas
                </h2>
              )}
              <div className="space-y-4">
                {devocionalesSinPlaylist.map((devocional) => (
                  <Card key={devocional.id} className="overflow-hidden hover:shadow-lg transition-shadow">
                    <CardContent className="p-0">
                      {/* Header */}
                      <div 
                        className="p-4 cursor-pointer hover:bg-amber-50 transition-colors"
                        onClick={() => setExpandedId(expandedId === devocional.id ? null : devocional.id)}
                      >
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 flex-wrap">
                              <h3 className="font-bold text-gray-800 text-lg">{devocional.titulo}</h3>
                              {devocional.zoom_url && (
                                <span className="px-2 py-0.5 bg-red-100 text-red-700 text-xs rounded-full flex items-center gap-1">
                                  <Radio className="w-3 h-3" /> EN VIVO
                                </span>
                              )}
                              {devocional.video_url && (
                                <span className="px-2 py-0.5 bg-purple-100 text-purple-700 text-xs rounded-full flex items-center gap-1">
                                  <Video className="w-3 h-3" /> Video
                                </span>
                              )}
                            </div>
                            
                            {devocional.cita_biblica && (
                              <p className="text-amber-700 font-medium text-sm mb-2">
                                📖 {devocional.cita_biblica}
                              </p>
                            )}
                            
                            <div className="flex flex-wrap items-center gap-3 text-xs text-gray-500">
                              {devocional.autor && (
                                <span className="flex items-center gap-1">
                                  <User className="w-3 h-3" />
                                  {devocional.autor}
                                </span>
                              )}
                              <span className="flex items-center gap-1">
                                <Calendar className="w-3 h-3" />
                                {devocional.fecha_creacion ? new Date(devocional.fecha_creacion).toLocaleDateString('es-UY') : 'Sin fecha'}
                              </span>
                            </div>
                          </div>
                          
                          <div className="flex items-center gap-2">
                            {user?.rol === 'admin' && (
                              <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                                <button
                                  onClick={() => openEditModal(devocional)}
                                  className="text-blue-500 hover:text-blue-700 p-1"
                                  title="Editar"
                                >
                                  <Edit className="w-4 h-4" />
                                </button>
                                <button
                                  onClick={() => handleDeleteDevocional(devocional.id)}
                                  className="text-red-500 hover:text-red-700 p-1"
                                  title="Eliminar"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              </div>
                            )}
                            {expandedId === devocional.id ? (
                              <ChevronUp className="w-5 h-5 text-gray-400" />
                            ) : (
                              <ChevronDown className="w-5 h-5 text-gray-400" />
                            )}
                          </div>
                        </div>
                      </div>
                      
                      {/* Contenido expandido */}
                      {expandedId === devocional.id && (
                        <div className="border-t border-gray-100 bg-gradient-to-br from-amber-50 to-orange-50 p-4">
                          {(devocional.zoom_url || devocional.video_url) && (
                            <div className="flex flex-wrap gap-3 mb-4">
                              {devocional.zoom_url && (
                                <a
                                  href={devocional.zoom_url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium"
                                >
                                  <Video className="w-4 h-4" />
                                  Unirse a Zoom EN VIVO
                                </a>
                              )}
                              {devocional.video_url && (
                                <button
                                  onClick={() => openVideoPlayer(devocional, false)}
                                  className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 text-sm font-medium"
                                >
                                  <Video className="w-4 h-4" />
                                  Ver Video
                                </button>
                              )}
                            </div>
                          )}
                          
                          {devocional.versiculo && (
                            <div className="bg-white rounded-lg p-4 mb-4 border-l-4 border-amber-500 shadow-sm">
                              <p className="text-gray-700 italic">&ldquo;{devocional.versiculo}&rdquo;</p>
                              {devocional.cita_biblica && (
                                <p className="text-amber-700 font-medium text-sm mt-2 text-right">
                                  — {devocional.cita_biblica}
                                </p>
                              )}
                            </div>
                          )}
                          
                          <div className="prose prose-sm max-w-none text-gray-700 whitespace-pre-wrap">
                            {devocional.contenido}
                          </div>
                          
                          {devocional.autor && (
                            <div className="mt-4 pt-4 border-t border-amber-200 text-right">
                              <span className="text-sm text-gray-600">— {devocional.autor}</span>
                            </div>
                          )}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          )}
        </>
      )}

      {/* Modal Añadir Devocional */}
      <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Plus className="w-5 h-5 text-amber-600" />
              Añadir Devocional
            </DialogTitle>
          </DialogHeader>
          
          {renderForm()}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddModal(false)}>Cancelar</Button>
            <Button
              onClick={handleSaveDevocional}
              disabled={saving}
              className="bg-amber-600 hover:bg-amber-700"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
              Guardar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal Editar Devocional */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit className="w-5 h-5 text-blue-600" />
              Editar Devocional
            </DialogTitle>
          </DialogHeader>
          
          {renderForm()}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditModal(false)}>Cancelar</Button>
            <Button
              onClick={handleUpdateDevocional}
              disabled={saving}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
              Actualizar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal Reproductor de Video/Playlist */}
      <Dialog open={showVideoModal} onOpenChange={setShowVideoModal}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto p-0">
          <div className="relative">
            <button
              onClick={() => setShowVideoModal(false)}
              className="absolute top-4 right-4 z-10 w-8 h-8 bg-black/50 hover:bg-black/70 rounded-full flex items-center justify-center text-white"
            >
              <X className="w-5 h-5" />
            </button>
            
            {selectedVideo && (
              <div>
                <div className="bg-black">
                  {selectedVideo.showPlaylist && selectedVideo.playlist_url ? (
                    renderEmbeddedPlayer(selectedVideo.playlist_url, true)
                  ) : selectedVideo.video_url ? (
                    renderEmbeddedPlayer(selectedVideo.video_url, false)
                  ) : null}
                </div>
                
                <div className="p-4">
                  <h2 className="text-xl font-bold text-gray-800 mb-2">{selectedVideo.titulo}</h2>
                  {selectedVideo.cita_biblica && (
                    <p className="text-amber-700 font-medium mb-2">📖 {selectedVideo.cita_biblica}</p>
                  )}
                  {selectedVideo.contenido && (
                    <p className="text-gray-600 text-sm mb-3 line-clamp-3">{selectedVideo.contenido}</p>
                  )}
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default GraduadosDevocionales;
